import { Navigate, useLocation, Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { isAdmin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Brand";

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="app-loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

export const Forbidden = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center" data-testid="forbidden-page">
    <Logo mark className="w-20" />
    <ShieldAlert className="h-8 w-8 text-destructive" />
    <div>
      <div className="eyebrow">Error 403</div>
      <h1 className="mt-2 text-3xl font-semibold">Access denied</h1>
      <p className="mt-2 text-muted-foreground">This area is restricted to lender administrators.</p>
    </div>
    <Button asChild data-testid="forbidden-go-portal">
      <Link to="/portal">Go to my loans</Link>
    </Button>
  </div>
);

export const RequireRole = ({ role, children }) => {
  const { user, preview } = useApp();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (role === "super_admin" && user.role !== "super_admin") return <Forbidden />;
  if (role === "admin" && !isAdmin(user)) return <Forbidden />;
  if (role === "borrower" && isAdmin(user)) return preview ? children : <Navigate to="/admin" replace />;
  return children;
};

export const RequireTempPassword = ({ children }) => {
  const { user } = useApp();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;
  return children;
};

export const HomeRedirect = () => {
  const { user } = useApp();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isAdmin(user) ? "/admin" : "/portal"} replace />;
};
